<script setup lang="ts">
import { ArrowRight, Braces, Clock3, Network, Search, ShieldCheck, Wrench } from "@lucide/vue";
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import LoadingState from "../components/LoadingState.vue";
import { useContent } from "../composables/useContent";
import { getDisplayTitle, getPrimaryLabel, matchesRecord } from "../lib/content";

const route = useRoute();
const router = useRouter();
const { records, loading, error } = useContent();
const query = ref(String(route.query.q ?? ""));
const category = ref(String(route.query.category ?? "all"));
const directoryRecords = computed(() => records.value.filter((record) => record.content_type === "directory_entry"));
const categories = computed(() => [...new Set(directoryRecords.value.map((record) => getPrimaryLabel(record)))].sort((a, b) => a.localeCompare(b, "zh-CN")));
const filtered = computed(() => directoryRecords.value
  .filter((record) => matchesRecord(record, { query: query.value, category: category.value }))
  .filter((record) => record.editorial_status === "published")
  .sort((a, b) => a.title.localeCompare(b.title, "zh-CN")));
const verifiedCount = computed(() => directoryRecords.value.filter((record) => record.editorial_status === "published").length);
const latestReviewDate = computed(() => directoryRecords.value
  .map((record) => record.last_reviewed_at)
  .filter((date): date is string => Boolean(date))
  .sort()
  .at(-1)
  ?.replaceAll("-", ".") ?? "-");

watch([query, category], () => {
  const next: Record<string, string> = {};
  if (query.value.trim()) next.q = query.value.trim();
  if (category.value !== "all") next.category = category.value;
  if (JSON.stringify(next) !== JSON.stringify(route.query)) void router.replace({ query: next });
});
watch(() => route.query, (next) => {
  query.value = String(next.q ?? "");
  category.value = String(next.category ?? "all");
}, { deep: true });
</script>

<template>
  <header class="page-head">
    <div class="page-width page-head-grid">
      <div><span>在线工具</span><h1>先看用途，再打开官方入口</h1><p>本地工具直接在浏览器运行；导航条目保留用途、数据边界和官方地址，方便按场景比较。</p></div>
      <Wrench :size="34" aria-hidden="true" />
    </div>
  </header>

  <section class="directory-band">
    <div class="page-width">
      <div class="native-tools-list" aria-label="站内工具">
        <RouterLink class="native-tool-entry primary" to="/tools/cidr">
          <span class="native-tool-icon"><Network :size="25" /></span>
          <span><small>网络规划 · 本地运行</small><strong>CIDR 子网规划器</strong><em>计算 IPv4 / IPv6 边界，拆分子网并导出 CSV</em></span>
          <ArrowRight :size="19" />
        </RouterLink>
        <RouterLink class="native-tool-entry config" to="/tools/json-yaml">
          <span class="native-tool-icon"><Braces :size="25" /></span>
          <span><small>配置数据 · 本地运行</small><strong>JSON / YAML 工作台</strong><em>严格校验、格式化并双向转换，不上传内容</em></span>
          <ArrowRight :size="19" />
        </RouterLink>
        <RouterLink class="native-tool-entry time" to="/tools/time">
          <span class="native-tool-icon"><Clock3 :size="25" /></span>
          <span><small>时间与时区 · 本地运行</small><strong>时间戳与时区转换器</strong><em>Unix、RFC 3339 与 IANA 时区互转，明确处理 DST 冲突</em></span>
          <ArrowRight :size="19" />
        </RouterLink>
      </div>
      <div class="directory-trust"><ShieldCheck :size="18" /><span><strong>{{ verifiedCount }}</strong> 个入口已逐项验证</span><small>最近检查 {{ latestReviewDate }}</small></div>
      <div class="directory-tools">
        <label class="filter-search"><Search :size="18" /><input v-model="query" type="search" placeholder="搜索工具或使用场景" aria-label="搜索工具与资源" /></label>
        <label><span>分类</span><select v-model="category"><option value="all">全部分类</option><option v-for="item in categories" :key="item" :value="item">{{ item }}</option></select></label>
      </div>
      <div class="results-toolbar"><span>{{ filtered.length }} 个已核对入口</span></div>
      <LoadingState v-if="loading || error" :error="error" />
      <div v-else class="directory-list">
        <article v-for="record in filtered" :key="record.id" class="directory-row">
          <div class="directory-row-meta">
            <span>{{ getPrimaryLabel(record) }}</span>
          </div>
          <div class="directory-row-body">
            <h2>{{ getDisplayTitle(record) }}</h2>
            <p>{{ record.summary }}</p>
          </div>
          <RouterLink :to="`/entry/${record.id}`" :aria-label="`查看 ${getDisplayTitle(record)}`" title="查看详情"><ArrowRight :size="18" /></RouterLink>
        </article>
      </div>
    </div>
  </section>
</template>
